"use client";

import { useState, useRef, useEffect } from "react";

interface ExpandableDescriptionProps {
  html: string;
  maxLines?: number;
}

export function ExpandableDescription({
  html,
  maxLines = 4,
}: ExpandableDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const measure = () => {
      if (!contentRef.current) return;
      // Temporarily remove the clamp so we can measure the real height
      const el = contentRef.current;
      const prevDisplay = el.style.display;
      const prevClamp = el.style.webkitLineClamp;
      el.style.display = "";
      el.style.webkitLineClamp = "";
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const maxHeight = lineHeight * maxLines;
      const actualHeight = el.scrollHeight;
      el.style.display = prevDisplay;
      el.style.webkitLineClamp = prevClamp;
      // Tolerance of 4px to avoid false positives from sub-pixel rounding
      setNeedsTruncation(actualHeight - maxHeight > 4);
    };
    // Defer to next frame to let layout settle (fonts, images etc.)
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [html, maxLines]);

  return (
    <div>
      <div
        ref={contentRef}
        className={`text-gray-600 prose max-w-none overflow-hidden transition-all duration-300 ${
          !isExpanded && needsTruncation ? `line-clamp-${maxLines}` : ""
        }`}
        style={
          !isExpanded && needsTruncation
            ? {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
              }
            : {}
        }
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {needsTruncation && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-secondary hover:text-secondary/80 font-medium text-sm mt-2 transition-colors"
        >
          {isExpanded ? "Voir moins" : "Voir plus"}
        </button>
      )}
    </div>
  );
}
